/**
 * Premium search overlay.
 *
 * Opens a frosted overlay from any element with [data-search-open] (e.g. the
 * header search icon) instead of navigating to the search page. Handles Esc /
 * outside-click / close-button dismissal, body scroll lock, autofocus, a small
 * focus trap, and debounced live predictive product results fetched from
 * Shopify's Predictive Search API (rendered by sections/predictive-search.liquid).
 *
 * The <form> still submits natively to /search?q=..., so search works without
 * JS and pressing Enter always goes to the full results page.
 */
(function () {
  var overlay = document.querySelector('[data-search-overlay]');
  if (!overlay) return;

  var input = overlay.querySelector('[data-search-input]');
  var results = overlay.querySelector('[data-predictive-results]');
  var predictiveUrl = overlay.getAttribute('data-predictive-url') || '/search/suggest';
  var triggers = document.querySelectorAll('[data-search-open]');

  var MIN_QUERY = 2;
  var DEBOUNCE_MS = 220;
  var debounceId = null;
  var controller = null;
  var lastTrigger = null;

  function isOpen() {
    return overlay.classList.contains('is-open');
  }

  function clearResults() {
    results.innerHTML = '';
  }

  function open(event) {
    if (event) {
      event.preventDefault();
      lastTrigger = event.currentTarget;
    }
    if (isOpen()) return;
    overlay.classList.add('is-open');
    overlay.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('search-overlay-open');
    // Focus once the overlay is painted so the caret lands reliably.
    window.requestAnimationFrame(function () {
      input.focus();
    });
  }

  function close() {
    if (!isOpen()) return;
    overlay.classList.remove('is-open');
    overlay.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('search-overlay-open');
    if (debounceId) {
      clearTimeout(debounceId);
      debounceId = null;
    }
    if (controller) {
      controller.abort();
      controller = null;
    }
    if (lastTrigger && typeof lastTrigger.focus === 'function') {
      lastTrigger.focus();
    }
  }

  function fetchPredictive(term) {
    if (controller) controller.abort();
    controller = 'AbortController' in window ? new AbortController() : null;

    var url =
      predictiveUrl +
      '?q=' + encodeURIComponent(term) +
      '&resources[type]=product' +
      '&resources[limit]=6' +
      '&section_id=predictive-search';

    fetch(url, controller ? { signal: controller.signal } : undefined)
      .then(function (response) {
        return response.ok ? response.text() : '';
      })
      .then(function (html) {
        // Ignore a stale response if the field was cleared meanwhile.
        if (input.value.trim().length < MIN_QUERY) {
          clearResults();
          return;
        }
        results.innerHTML = html ? html.trim() : '';
      })
      .catch(function () {
        /* Aborted request or network error: leave existing results untouched. */
      });
  }

  function onInput() {
    var term = input.value.trim();
    if (debounceId) clearTimeout(debounceId);
    if (term.length < MIN_QUERY) {
      if (controller) {
        controller.abort();
        controller = null;
      }
      clearResults();
      return;
    }
    debounceId = setTimeout(function () {
      fetchPredictive(term);
    }, DEBOUNCE_MS);
  }

  function trapFocus(event) {
    var focusable = overlay.querySelectorAll(
      'a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])'
    );
    focusable = Array.prototype.filter.call(focusable, function (el) {
      return el.offsetWidth > 0 || el.offsetHeight > 0;
    });
    if (!focusable.length) return;

    var first = focusable[0];
    var last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  // Open from any trigger (header icon, etc.).
  Array.prototype.forEach.call(triggers, function (trigger) {
    trigger.addEventListener('click', open);
  });

  // Close from the X button and the backdrop.
  Array.prototype.forEach.call(overlay.querySelectorAll('[data-search-close]'), function (el) {
    el.addEventListener('click', function (event) {
      event.preventDefault();
      close();
    });
  });

  // Esc to close, Tab to stay trapped inside the dialog.
  document.addEventListener('keydown', function (event) {
    if (!isOpen()) return;
    if (event.key === 'Escape') {
      close();
    } else if (event.key === 'Tab') {
      trapFocus(event);
    }
  });

  input.addEventListener('input', onInput);
})();
